import { useCallback, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Dialog, DialogContent } from '@scio3/ui';
import { CANCEL_SUBSCRIPTION, GET_APP_CFG } from '@scio3/graphql-client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useTeamStore } from '@/stores/team-store';
import { useAuthStore } from '@/stores/auth-store';
import { Loader2 } from 'lucide-react';

type Props = {
  open: boolean;
  onClose: (submitted?: boolean) => void;
  subAccountId?: string;
};

/** Load TypeForm SDK script, returns a promise that resolves when loaded */
function loadTypeFormScript(): Promise<void> {
  return new Promise((resolve) => {
    if ((window as unknown as Record<string, unknown>).tf) {
      resolve();
      return;
    }
    document.querySelector('script[src*="embed.typeform.com"]')?.remove();
    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.body.appendChild(script);
  });
}

/**
 * TypeForm-based cancel subscription dialog — matching scio1's typeFormCancelSubscriptionModal.
 * Extracts widget ID from AppCfgs embed HTML, creates the TypeForm div programmatically,
 * and uses the TypeForm SDK to initialize it.
 */
export function CancelSubscriptionDialog({ open, onClose, subAccountId }: Props) {
  const { t } = useTranslation();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const apolloClient = useApolloClient();
  // Counter increments on each close — forces fresh remount of container div on reopen
  const [mountKey, setMountKey] = useState(0);

  // #1113 — TypeForm only collects survey answers; nothing in the dialog
  // actually cancels the subscription. We call the backend mutation here so
  // `payment.scheduleCancel` is set synchronously and the Subscription page
  // flips to "scheduled to end on …" + Resume Subscription. The TypeForm
  // server-side webhook continues to capture survey answers in parallel.
  const [cancelSubscriptionMutation] = useMutation(CANCEL_SUBSCRIPTION);

  const { data: cfgData } = useQuery<{ getAppCfgs: Record<string, unknown> }>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId,
  });

  const embedBase64 = cfgData?.getAppCfgs?.typeFormCancelSubscriptionEmbed as string | undefined;

  const widgetId = (() => {
    if (!embedBase64) return null;
    try {
      const html = atob(embedBase64);
      const match = html.match(/data-tf-widget="([^"]+)"/);
      return match?.[1] ?? null;
    } catch {
      return null;
    }
  })();

  const handleClose = useCallback(
    async (submitted = false) => {
      if (submitted) {
        try {
          // Backend ValidationPipe requires a populated SubmitUserSurveyInput
          // even when answers are captured server-side via TypeForm's webhook.
          // Pass a stub so the mutation passes validation; the cancellation
          // itself is what we care about here.
          const variables: Record<string, unknown> = {
            survey: {
              formId: 'cancelSubscription',
              formVersion: 1,
              formType: 'cancelSubscription',
              questions: [],
            },
          };
          if (subAccountId) variables.subAccountId = subAccountId;
          const result = await cancelSubscriptionMutation({ variables });
          const response = (
            result.data as Record<string, { status?: boolean; message?: string } | undefined>
          )?.cancelSubscription;
          if (!response?.status) {
            console.error('cancelSubscription failed', response);
            toast.error(response?.message ?? t('Failed to cancel subscription'));
          } else {
            // Refetch the company / sub-account info so the Subscription page
            // picks up the new `payment.scheduleCancel` immediately. Also
            // refresh GetUpcomingSubscription + GetSubscriptionInfoStripe so
            // the "Upcoming charge" amount drops to $0.00 in the same render
            // as the "Resume Subscription" CTA appearing — matches the
            // refetch list used by RESUME_SUBSCRIPTION on the inverse path.
            apolloClient.refetchQueries({
              include: [
                'GetUpcomingSubscription',
                'GetSubscriptionInfoStripe',
                ...(subAccountId ? ['GetSubAccountInfo', 'GetSubAccountList'] : ['GetCompanyInfo']),
              ],
            });
          }
        } catch (err) {
          console.error('cancelSubscription threw', err);
          toast.error((err as { message?: string })?.message ?? t('Failed to cancel subscription'));
        }
      }
      setMountKey((k) => k + 1);
      onClose(submitted);
    },
    [onClose, cancelSubscriptionMutation, subAccountId, apolloClient, t],
  );

  // Callback ref — fires when container mounts in DOM (after Dialog portal renders)
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !widgetId || !me || !company) return;

      const meAny = me as unknown as Record<string, unknown>;
      const companyAny = company as unknown as Record<string, unknown>;
      const hiddenParts = [
        `email=${meAny.email ?? ''}`,
        `user_id=${meAny._id ?? ''}`,
        `name=${(meAny.profile as Record<string, unknown> | undefined)?.firstName ?? ''}`,
        `account_id=${subAccountId ?? companyAny._id ?? ''}`,
        `sub_qty=${(companyAny.limitation as Record<string, unknown> | undefined)?.maxQuantity ?? 0}`,
      ];

      const div = document.createElement('div');
      div.setAttribute('data-tf-widget', widgetId);
      div.setAttribute('data-tf-iframe-props', 'title=Cancel Subscription');
      div.setAttribute('data-tf-medium', 'snippet');
      div.setAttribute('data-tf-on-ending-button-click', 'clicked');
      div.setAttribute('data-tf-hidden', hiddenParts.join(','));
      div.style.width = '100%';
      div.style.height = '100%';
      node.appendChild(div);

      (window as unknown as Record<string, unknown>).clicked = () => {
        handleClose(true);
      };

      loadTypeFormScript().then(() => {
        const tf = (window as unknown as Record<string, unknown>).tf as
          | { load?: () => void }
          | undefined;
        tf?.load?.();
      });
    },
    [widgetId, me, company, subAccountId, handleClose],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose(false);
      }}
    >
      <DialogContent
        className="h-[80vh] max-w-3xl p-0"
        aria-describedby={undefined}
        data-testid="cancel-subscription-dialog"
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
        <div key={mountKey} ref={containerRef} className="relative h-full w-full" />
      </DialogContent>
    </Dialog>
  );
}
